import * as THREE from 'three';
import { simToWorld } from '@somnhold/render';
import type { BattleState } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h, viewH, viewW } from '../ui/dom.ts';

/** A chevron pointing down; turned to point at a place off screen (as the crypt's arrows). */
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

/**
 * The pit in the woods (Caras prestadas 3), and the clan's pen on the drive (El Cuerno 1): one arrow, over the pit while someone is still in it and nobody follows
 * this hero, over the clearing while someone does; held at the screen's edge when the place is off screen.
 */
export class RescueWay {
  readonly el = h('div.dg-hud.pl-ways');
  private way = h('div.dg-way', {}, h('span.dg-way-arrow', { html: CHEVRON }), h('span.dg-way-txt'));

  constructor(
    private s: () => BattleState,
    private me: string,
  ) {
    this.el.append(this.way);
  }

  place(camera: THREE.Camera): void {
    const s = this.s();
    const r = s.rules?.rescue;
    const st = s.rescue;
    // The Prestacaras: an arrow to it while it is off screen (it comes out far away, in the dark).
    const boss = s.rules?.lanterns ? s.enemies.find((e) => e.mask && e.hp > 0) : undefined;
    if (boss && s.result === 'running') {
      (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = t('side.wayBoss');
      this.point(boss, camera, true);
      return;
    }
    // The blasting: carrying a barrel, to the nearest open crack; else to a barrel on the ground, if there is one.
    const blast = s.rules?.blast;
    if (blast) {
      const st = s.blast;
      const mine = s.heroes.find((x) => x.pid === this.me);
      const d2 = (p: { x: number; y: number }): number => (mine ? (p.x - mine.x) ** 2 + (p.y - mine.y) ** 2 : 0);
      const carrying = !!st?.barrels.some((b) => b.pid === this.me);
      const pool = carrying ? blast.cracks.filter((_, i) => !st?.sealed[i]) : (st?.barrels ?? []).filter((b) => !b.pid && !b.fuse);
      const at = mine ? [...pool].sort((a, b) => d2(a) - d2(b))[0] : undefined;
      const close = !!at && d2(at) < (carrying ? blast.plant : blast.reach) ** 2;
      this.way.hidden = !at || close || s.result !== 'running' || !!st?.lit;
      if (this.way.hidden || !at) return;
      (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = carrying ? t('side.wayCrack') : t('side.wayBarrel');
      this.point(at, camera, false);
      return;
    }
    // The alarm bells: to the nearest lever still up.
    const bells = s.rules?.bells;
    if (bells) {
      const st = s.bells;
      const mine = s.heroes.find((x) => x.pid === this.me);
      const up = bells.rooms.filter((_, i) => !st?.down[i]).map((r) => r.lever);
      const at = mine ? up.sort((a, b) => (a.x - mine.x) ** 2 + (a.y - mine.y) ** 2 - ((b.x - mine.x) ** 2 + (b.y - mine.y) ** 2))[0] : undefined;
      const close = !!at && !!mine && (mine.x - at.x) ** 2 + (mine.y - at.y) ** 2 < bells.reach ** 2;
      this.way.hidden = !at || close || s.result !== 'running';
      if (this.way.hidden || !at) return;
      (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = t('side.wayLever');
      this.point(at, camera, false);
      return;
    }
    // The old trap: to a winch still to wind, and once it is wound, to the lever.
    const trap = s.rules?.trap;
    if (trap) {
      const st = s.trap;
      const mine = s.heroes.find((x) => x.pid === this.me);
      const open = trap.winches.filter((_, i) => (st?.wound[i] ?? 0) < trap.wind);
      const at = st?.fireAt ? undefined : st?.armed ? trap.lever : mine ? open.sort((a, b) => (a.x - mine.x) ** 2 + (a.y - mine.y) ** 2 - ((b.x - mine.x) ** 2 + (b.y - mine.y) ** 2))[0] : undefined;
      const close = !!at && !!mine && (mine.x - at.x) ** 2 + (mine.y - at.y) ** 2 < trap.reach ** 2;
      this.way.hidden = !at || close || s.result !== 'running';
      if (this.way.hidden || !at) return;
      (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = st?.armed ? t('side.wayLever') : t('side.wayWinch');
      this.point(at, camera, false);
      return;
    }
    // The collapse: to the firm rock while a scout follows this hero; else to the nearest one still lying on the glass.
    const col = s.rules?.collapse;
    if (col) {
      const mine = s.heroes.find((x) => x.pid === this.me);
      const people = s.collapse?.people ?? [];
      const carrying = people.some((p) => p.pid === this.me && !p.safe && p.hp > 0);
      const lying = people.filter((p) => !p.pid && p.hp > 0 && !p.safe);
      let at: { x: number; y: number } | undefined;
      if (carrying) at = { x: Math.trunc((col.ax * (col.rock - 1200)) / 10000), y: Math.trunc((col.ay * (col.rock - 1200)) / 10000) };
      else if (mine) at = lying.sort((a, b) => (a.x - mine.x) ** 2 + (a.y - mine.y) ** 2 - ((b.x - mine.x) ** 2 + (b.y - mine.y) ** 2))[0];
      const close = !!at && !!mine && (mine.x - at.x) ** 2 + (mine.y - at.y) ** 2 < col.lift ** 2;
      this.way.hidden = !at || close || s.result !== 'running';
      if (this.way.hidden || !at) return;
      (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = carrying ? t('side.wayRock') : t('side.wayScout');
      this.point(at, camera, false);
      return;
    }
    // The drive: an arrow to the clan's pen while any boar is still out.
    const herd = s.rules?.herd;
    if (herd) {
      const out = (s.herd?.boars.length ?? herd.boars) > (s.herd?.penned ?? 0);
      this.way.hidden = !out || s.result !== 'running';
      if (this.way.hidden) return;
      (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = t('side.wayPen');
      this.point(herd.pen, camera, false);
      return;
    }
    if (!r || s.result !== 'running') {
      this.way.hidden = true;
      return;
    }
    const following = st?.people.some((p) => p.pid === this.me) ?? false;
    const toPit = !following && (st?.left ?? r.people) > 0;
    const at = toPit ? r.pit : { x: 0, y: 0 };
    const mine = s.heroes.find((x) => x.pid === this.me);
    // Hidden once the hero is there (the ring on the ground says the rest).
    const close = mine && (mine.x - at.x) ** 2 + (mine.y - at.y) ** 2 < (toPit ? r.free : r.home) ** 2;
    this.way.hidden = !!close || (!toPit && !following);
    if (this.way.hidden) return;
    (this.way.querySelector('.dg-way-txt') as HTMLElement).textContent = toPit ? t('side.wayPit') : t('side.wayHome');
    this.point(at, camera, false);
  }

  /** Over a place, or held at the screen's edge pointing to it (`edgeOnly`: hidden while it is in sight). */
  private point(at: { x: number; y: number }, camera: THREE.Camera, edgeOnly: boolean): void {
    const W = viewW();
    const H = viewH();
    const v = simToWorld(at.x, at.y, new THREE.Vector3()).setY(1.2).project(camera);
    const behind = v.z > 1;
    let px = (v.x * 0.5 + 0.5) * W;
    let py = (-v.y * 0.5 + 0.5) * H;
    if (behind) {
      px = W - px;
      py = H - py;
    }
    const m = 46;
    const top = W <= 640 ? 200 : 120;
    const inView = !behind && px > m && px < W - m && py > top && py < H - m;
    this.way.hidden = edgeOnly && inView;
    this.way.classList.toggle('edge', !inView);
    let turn = 0;
    // In sight, it floats above the place instead of covering it.
    if (inView) py -= 70;
    if (!inView) {
      const cx = W / 2;
      const cy = (top + H - m) / 2;
      const dx = px - cx;
      const dy = py - cy;
      const k = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), (H - m - top) / 2 / Math.max(1, Math.abs(dy)));
      px = cx + dx * k;
      py = cy + dy * k;
      turn = Math.atan2(dy, dx) - Math.PI / 2;
    }
    this.way.style.transform = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -50%)`;
    (this.way.querySelector('.dg-way-arrow') as HTMLElement).style.transform = `rotate(${turn.toFixed(3)}rad)`;
  }
}
